// onsite-test-toggle.js
// Floating QA widget for forcing any on-site edit test to Control or Variant.
//
// How it works:
//   Renders a draggable overlay listing every test from window.__onsiteTestConfig.
//   Each test has a dropdown — selecting "Variant" writes `__onsite_test__{slug} = "on"`
//   to localStorage; selecting "Control" writes "off". The page reloads after each
//   change so the dispatcher picks up the new state fresh.
//   Hide tests collapses the panel without changing assignments. Show tests
//   restores it; each panel remembers that preference across page reloads.
//
// Two safety guards:
//   1. Renders for the theme QA setting or scoped draft/development product previews.
//   2. Always suppresses itself on the published live theme (Shopify.theme.role === "main").
//   In either suppression case, all localStorage overrides are also cleared automatically
//   so a QA session can never accidentally bleed into production or persist after the
//   QA controls are disabled. Collapsing the panel keeps the QA assignments.

(() => {
	const LS_PREFIX = "__onsite_test__";
	const POS_KEY = "__onsite_test_toggle_pos__";
	const STATES = [
		{ value: "off", label: "Control" },
		{ value: "A", label: "Variant A" },
		{ value: "B", label: "Variant B" },
	];

	function statesFor(test) {
		return Array.isArray(test.qaStates) && test.qaStates.length
			? test.qaStates
			: STATES;
	}

	function isLiveTheme() {
		return (
			typeof Shopify !== "undefined" &&
			Shopify.theme &&
			Shopify.theme.role === "main"
		);
	}

	function getState(slug) {
		try {
			const v = localStorage.getItem(LS_PREFIX + slug);
			const variant = localStorage.getItem(LS_PREFIX + slug + "__variant");
			if (v === "on" && (variant === "A" || variant === "B" || variant === "C")) return variant;
			if (v === "on") return "B";
		} catch (_) {}
		return "off";
	}

	function setState(slug, state) {
		try {
			if (state === "off") {
				localStorage.setItem(LS_PREFIX + slug, "off");
				localStorage.removeItem(LS_PREFIX + slug + "__variant");
			} else {
				localStorage.setItem(LS_PREFIX + slug, "on");
				localStorage.setItem(LS_PREFIX + slug + "__variant", state);
			}
		} catch (_) {}
	}

	function getSavedPos() {
		try {
			const raw = localStorage.getItem(POS_KEY);
			return raw ? JSON.parse(raw) : null;
		} catch (_) {
			return null;
		}
	}

	function savePos(top, left) {
		try {
			localStorage.setItem(POS_KEY, JSON.stringify({ top, left }));
		} catch (_) {}
	}

	function makeDraggable(el, handle) {
		let dragging = false;
		let startX = 0;
		let startY = 0;
		let startLeft = 0;
		let startTop = 0;
		let didMove = false;

		handle.style.cursor = "grab";
		handle.style.touchAction = "none";

		handle.addEventListener("pointerdown", (e) => {
			if (e.target.closest("button, select, input, a")) return;
			dragging = true;
			didMove = false;
			startX = e.clientX;
			startY = e.clientY;
			startLeft = parseInt(el.style.left, 10) || 0;
			startTop = parseInt(el.style.top, 10) || 0;
			handle.setPointerCapture(e.pointerId);
			handle.style.cursor = "grabbing";
			e.preventDefault();
		});

		handle.addEventListener("pointermove", (e) => {
			if (!dragging) return;
			e.preventDefault();
			const dx = e.clientX - startX;
			const dy = e.clientY - startY;
			if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didMove = true;
			const newLeft = Math.min(
				Math.max(0, startLeft + dx),
				Math.max(0, window.innerWidth - el.offsetWidth),
			);
			const newTop = Math.min(
				Math.max(0, startTop + dy),
				Math.max(0, window.innerHeight - el.offsetHeight - 64),
			);
			el.style.left = newLeft + "px";
			el.style.top = newTop + "px";
		});

		const finishDrag = () => {
			if (!dragging) return;
			dragging = false;
			handle.style.cursor = "grab";
			if (didMove) {
				savePos(parseInt(el.style.top, 10), parseInt(el.style.left, 10));
			}
		};
		handle.addEventListener("pointerup", finishDrag);
		handle.addEventListener("pointercancel", finishDrag);
	}

	function clearAllOverrides() {
		try {
			const config = window.__onsiteTestConfig || [];
			config.forEach(({ slug }) => {
				localStorage.removeItem(LS_PREFIX + slug);
				localStorage.removeItem(LS_PREFIX + slug + "__variant");
			});
		} catch (_) {}
	}

	function createToggle() {
		const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
		const previewSlugs = role === "development" || role === "unpublished"
			? window.__onsiteTestPreviewSlugs || [] : [];
		if (!window.__onsiteTestToggleEnabled && !previewSlugs.length) {
			clearAllOverrides();
			return;
		}
		if (isLiveTheme()) {
			console.warn(
				"wool onsite-test-toggle: live theme detected; toggle suppressed.",
			);
			clearAllOverrides();
			return;
		}

		const allTests = window.__onsiteTestConfig || [];
		createWidget(allTests.filter(test => previewSlugs.includes(test.slug)), true);
		if (window.__onsiteTestToggleEnabled) {
			createWidget(allTests.filter(test => !previewSlugs.includes(test.slug)), false);
		}
	}

	function createWidget(config, compactPreview) {
		if (!config.length) return;

		const savedPos = getSavedPos();
		const defaultTop = 120;
		const defaultLeft = 20;
		const collapsedKey = "__onsite_test_toggle_collapsed__" + (compactPreview ? "preview" : "main");
		let collapsed = false;
		try { collapsed = localStorage.getItem(collapsedKey) === "1"; } catch (_) {}

		const wrapper = document.createElement("div");
		wrapper.className = "onsite-test-toggle";
		wrapper.style.cssText = `
			position: fixed;
			top: ${savedPos ? savedPos.top : defaultTop}px;
			left: ${savedPos ? savedPos.left : defaultLeft}px;
			z-index: 99999;
			background: #0c1a94;
			color: white;
			padding: 12px 16px;
			border-radius: 8px;
			font-size: 12px;
			font-weight: 600;
			font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
			box-shadow: 0 4px 12px rgba(0,0,0,0.15);
			display: flex;
			flex-direction: column;
			gap: 8px;
			width: 440px;
			max-width: calc(100vw - 24px);
			box-sizing: border-box;
		`;
		const expandedStyle = wrapper.style.cssText;
		const header = document.createElement("div");
		header.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:12px;";
		const heading = document.createElement("span");
		heading.textContent = compactPreview ? "Preview tests" : "On-site tests";
		header.appendChild(heading);
		const collapseButton = document.createElement("button");
		collapseButton.type = "button";
		collapseButton.dataset.onsiteTestsCollapse = "";
		collapseButton.style.cssText = "min-height:44px;padding:8px 12px;border:1px solid #ffffff66;border-radius:7px;background:#0c1a94;color:#fff;font:inherit;cursor:pointer;white-space:nowrap;";
		header.appendChild(collapseButton);
		wrapper.appendChild(header);
		const controls = document.createElement("div");
		controls.id = compactPreview ? "onsite-test-preview-options" : "onsite-test-options";
		controls.style.cssText = "display:flex;flex-direction:column;gap:8px;max-height:calc(100dvh - 220px);overflow-y:auto;overscroll-behavior:contain;";
		collapseButton.setAttribute("aria-controls", controls.id);
		wrapper.appendChild(controls);

		if (compactPreview) {
			wrapper.dataset.addonStylePreview = "";
			wrapper.style.top = "auto";
			wrapper.style.bottom = "80px";
			wrapper.style.left = "16px";
			wrapper.style.width = "300px";
			wrapper.style.maxWidth = "calc(100vw - 32px)";
			wrapper.style.boxSizing = "border-box";
		}
		let panelStyle = compactPreview ? wrapper.style.cssText : expandedStyle;
		if (!compactPreview) makeDraggable(wrapper, header);

		config.forEach((test) => {
			const states = statesFor(test);
			let current = getState(test.slug);
			if (!states.some((state) => state.value === current)) {
				current = states[0].value;
			}

			const row = document.createElement("div");
			row.style.cssText = compactPreview
				? "display:flex;flex-direction:column;gap:8px;"
				: "display:grid;grid-template-columns:minmax(0,.8fr) minmax(0,1.2fr);align-items:center;gap:8px;";

			const label = document.createElement("label");
			label.htmlFor = "onsite-test-select-" + test.slug;
			label.textContent = test.slug === "addon-styles" ? "Add-on styles · Preview" : test.slug + ": ";
			label.style.overflowWrap = "anywhere";
			row.appendChild(label);

			const select = document.createElement("select");
			select.id = label.htmlFor;
			select.style.cssText = `
				margin-left: 0;
				padding: 4px 8px;
				border-radius: 4px;
				border: none;
				font-size: 12px;
				cursor: pointer;
				background: white;
				color: #0c1a94;
				font-weight: 500;
				width: 100%;
				min-width: 0;
				box-sizing: border-box;
			`;
			if (compactPreview) {
				select.style.width = "100%";
				select.style.minWidth = "0";
			}

			states.forEach(({ value, label }) => {
				const option = document.createElement("option");
				option.value = value;
				option.textContent = label;
				option.selected = current === value;
				select.appendChild(option);
			});

			select.addEventListener("change", (e) => {
				setState(test.slug, e.target.value);
				window.location.reload();
			});

			row.appendChild(select);
			controls.appendChild(row);
		});
		if (compactPreview) {
			const help = document.createElement("span");
			help.textContent = "Choose a style, then Add to Cart.";
			help.style.cssText = "font-size:11px;font-weight:400;line-height:1.5;";
			controls.appendChild(help);
		}

		document.body.appendChild(wrapper);
		const keepInView = () => {
			if (!compactPreview && !collapsed) {
				const left = parseFloat(wrapper.style.left);
				const top = parseFloat(wrapper.style.top);
				const bounds = wrapper.getBoundingClientRect();
				wrapper.style.left = Math.max(0, Math.min(Number.isFinite(left) ? left : defaultLeft, window.innerWidth - Math.ceil(bounds.width))) + "px";
				wrapper.style.top = Math.max(0, Math.min(Number.isFinite(top) ? top : defaultTop, window.innerHeight - Math.ceil(bounds.height) - 64)) + "px";
			}
		};
		const renderPanel = () => {
			controls.hidden = collapsed;
			controls.style.display = collapsed ? "none" : "flex";
			heading.hidden = collapsed;
			collapseButton.textContent = collapsed ? "Show tests" : "Hide tests";
			collapseButton.setAttribute("aria-expanded", String(!collapsed));
			if (collapsed) {
				wrapper.style.cssText = `position:fixed;left:12px;bottom:calc(${compactPreview ? 132 : 80}px + env(safe-area-inset-bottom, 0px));z-index:99999;padding:0;border-radius:8px;background:#0c1a94;color:#fff;font:600 12px -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;box-shadow:0 3px 10px #0003;`;
			} else {
				wrapper.style.cssText = panelStyle;
			}
			keepInView();
		};
		collapseButton.addEventListener("click", () => {
			if (!collapsed) panelStyle = wrapper.style.cssText;
			collapsed = !collapsed;
			try { localStorage.setItem(collapsedKey, collapsed ? "1" : "0"); } catch (_) {}
			renderPanel();
		});
		renderPanel();
		window.addEventListener("resize", keepInView);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", createToggle);
	} else {
		createToggle();
	}
})();
